import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { fetchSiteMedia, fetchStoreInfo } from '../services/api';

const AboutPage = () => {
  const { language, t } = useLanguage();
  const [imgUrl, setImgUrl] = useState('/about-story.jpg');
  const [storeInfo, setStoreInfo] = useState({});

  useEffect(() => {
    fetchSiteMedia().then((m) => {
      if (m && m.about_story) setImgUrl(m.about_story);
    });
    fetchStoreInfo().then((s) => setStoreInfo(s)).catch(() => {});
  }, []);

  // Build values list from individually editable text keys
  const values = [1, 2, 3].map((i) => ({
    title: t(`about.value${i}.title`),
    description: t(`about.value${i}.description`),
  }));

  const firstAddress = (storeInfo.addresses && storeInfo.addresses[0]) || null;
  const addressText = firstAddress
    ? (language === 'tr' ? firstAddress.address_tr : firstAddress.address_en)
    : (language === 'tr' ? storeInfo.address : storeInfo.addressEn);

  const toEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('/embed')) return url;
    const at = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+(?:\.\d+)?)z)?/);
    if (at) {
      const [, lat, lng, zoom = '15'] = at;
      return `https://www.google.com/maps?q=${lat},${lng}&z=${Math.round(parseFloat(zoom))}&output=embed`;
    }
    try {
      const u = new URL(url);
      const q = u.searchParams.get('q') || u.pathname.split('/place/')[1]?.split('/')[0];
      if (q) return `https://www.google.com/maps?q=${encodeURIComponent(decodeURIComponent(q))}&output=embed`;
    } catch (_) { /* ignore */ }
    return null;
  };

  const mapEmbed = firstAddress ? toEmbedUrl(firstAddress.maps_url) : null;

  return (
    <div className="min-h-screen bg-[#fffef2]">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 bg-[#f6f5e8] relative overflow-hidden">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl">
              <span className="text-[#b8860b] text-sm font-medium tracking-widest uppercase mb-4 block">
                {t('about.subtitle')}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#333333] mb-8 tracking-wide leading-tight">
                {t('about.title')}
              </h1>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-24 lg:py-32 bg-[#fffef2]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
              {/* Image */}
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={imgUrl}
                    alt="Altınzade Craftsmanship"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-[#b8860b]/10 -z-10" />
              </div>

              {/* Content */}
              <div>
                <p className="text-[#333333] text-xl leading-relaxed mb-8" data-testid="about-story-p1">
                  {t('about.story.p1')}
                </p>
                <p className="text-[#666666] text-base leading-relaxed mb-8" data-testid="about-story-p2">
                  {t('about.story.p2')}
                </p>
                <p className="text-[#666666] text-base leading-relaxed" data-testid="about-story-p3">
                  {t('about.story.p3')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 lg:py-32 bg-[#f6f5e8]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-light text-[#333333] tracking-wide">
                {t('about.values.heading')}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {values.map((value, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 bg-[#b8860b]/10 flex items-center justify-center">
                    <span className="text-[#b8860b] text-3xl font-light">
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="text-[#333333] text-xl font-medium mb-4">
                    {value.title}
                  </h3>
                  <p className="text-[#666666] text-base leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Location Section */}
        <section className="py-24 lg:py-32 bg-[#fffef2]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-light text-[#333333] mb-8 tracking-wide">
                  {t('contact.visitUs')}
                </h2>
                <p className="text-[#666666] text-base leading-relaxed mb-6">
                  {addressText}
                </p>
                <p className="text-[#666666] text-base">
                  {storeInfo.whatsappDisplay || storeInfo.whatsapp}
                </p>
              </div>
              <div className="aspect-video bg-[#f6f5e8] overflow-hidden">
                {mapEmbed ? (
                  <iframe
                    src={mapEmbed}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Altınzade Location"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-center p-10 text-[#666] text-sm">
                    {language === 'tr'
                      ? 'Konum bilgisi yakında eklenecek.'
                      : 'Map coming soon.'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
